import React from 'react';

import {ResizableContainer, ResizablePanel} from 'components/ResizablePanel';
import {TableViewsWrapper, useTableViewsContext} from 'notebook/tables/table-views/TableViewsContext';

import {StructuredTableOutsideNotebookWithTableViews} from '../../structured-tables/StructuredTableOutsideNotebook';
import {FocusedTableViewMode} from '../TableConstants';

interface StructuredTableViewPanelProps {
  children: React.ReactNode;
}

const StructuredTableViewContent = ({children}: StructuredTableViewPanelProps): JSX.Element | null => {
  const {focusedStructuredTableCid, tablesByCid, focusedTableViewMode, setTableView} = useTableViewsContext();

  const structuredTableOutsideNotebookProps =
    focusedStructuredTableCid && tablesByCid[focusedStructuredTableCid]
      ? tablesByCid[focusedStructuredTableCid]
      : null;
  return (
    <ResizableContainer enabled={true} style={{flexGrow: 1, minHeight: '285px'}}>
      <ResizablePanel
        enabled={true}
        index={0}
        minSize={15}
        isRendered={true}
        isCollapsed={
          Boolean(focusedStructuredTableCid) && focusedTableViewMode === FocusedTableViewMode.FULLSCREEN
        }
        onCollapse={() => {
          setTableView(focusedStructuredTableCid, FocusedTableViewMode.FULLSCREEN);
        }}
        onExpand={() => {
          setTableView(focusedStructuredTableCid, FocusedTableViewMode.HORIZONTAL_SPLIT);
        }}
      >
        {children}
      </ResizablePanel>
      <ResizablePanel
        index={1}
        minSize={15}
        defaultSize={focusedTableViewMode === FocusedTableViewMode.FULLSCREEN ? 100 : 50}
        isRendered={focusedStructuredTableCid != null}
        onCollapse={() => {
          setTableView(null);
        }}
        onExpand={() => {
          if (focusedStructuredTableCid) {
            const table = $(`#${focusedStructuredTableCid}`);
            if (table.length > 0) {
              table[0].scrollIntoView({block: 'center'});
            }
          }
        }}
      >
        {focusedStructuredTableCid && structuredTableOutsideNotebookProps && (
          <div key="maximized-table" className="tableEditable-maximizedWrapper">
            <StructuredTableOutsideNotebookWithTableViews
              {...structuredTableOutsideNotebookProps}
              isFocused={false}
              isMaximized={focusedTableViewMode === FocusedTableViewMode.FULLSCREEN}
              isSplitScreen={focusedTableViewMode === FocusedTableViewMode.HORIZONTAL_SPLIT}
            />
          </div>
        )}
      </ResizablePanel>
    </ResizableContainer>
  );
};

/**
 * Wrapper for StructuredTableOutsideNotebook that provides Table Views
 * Put this wrapper around the page content where you would want the maximized table view to appear.
 *
 * Any StructuredTableOutsideNotebook inside this wrapper can be changed to
 * <StructuredTableOutsideNotebookWithTableViews> to enable table views.
 */

export const StructuredTableViewPanel = ({children}: StructuredTableViewPanelProps): JSX.Element | null => (
  <TableViewsWrapper>
    <StructuredTableViewContent>{children}</StructuredTableViewContent>
  </TableViewsWrapper>
);
